'use client'

/**
 * Overview v3 — Bill prototype 2026-05-30.
 *
 * Layout (top → bottom):
 *   • Eyebrow row       → OVERVIEW + meta
 *   • Header row        h1 + 'All channels' / 'Last N days' filters
 *   • KPI band          four KPI tiles
 *   • Channel section   '⌁ By channel' + 'Per-channel rollup' h2
 *   • Rollup cards      one per channel, expandable to show subs
 *
 * Each rollup card collapses/expands the subchannel list on click. Audio +
 * Retail render as muted (.muted) cards with a Marketplace CTA in place of KPIs.
 */

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { subDays } from 'date-fns'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  DollarCircleIcon,
  Target02Icon,
  ChartLineData01Icon,
  PlugSocketIcon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  SearchIcon,
  UserGroup03Icon,
  ModernTvIcon,
  HierarchyCircle03Icon,
  HeadphonesIcon,
  ShoppingCart01Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import type { IconSvgElement } from '@hugeicons/react'
import { useCachedUser } from '@/hooks/useCachedUser'
import {
  type OverviewChannel,
  type OverviewChannelKey,
  FALLBACK_OVERVIEW,
} from '@/lib/overview/fallback-data'
import { adaptOverviewData, type OverviewCatalogResponse } from '@/lib/overview/adapt'
import type { ChannelMetricsResponse } from '@/app/api/connectors/catalog/metrics/route'
import { ChannelFilter, type ChannelFilterValue } from '@/components/shared/ChannelFilter'
import { DateRangeFilter, type DateRangeValue } from '@/components/shared/DateRangeFilter'
import { SectionHeading, SectionLabel } from '@/components/shared/SectionLabel'
import { LobbyHeaderMeta } from '@/contexts/LobbyHeaderMetaContext'
import { cn } from '@/lib/utils'
import { DemoDataBadge } from '@/components/DemoDataBadge'

const CHANNEL_ICON: Record<OverviewChannelKey, IconSvgElement> = {
  search: SearchIcon,
  social: UserGroup03Icon,
  tv: ModernTvIcon,
  programmatic: HierarchyCircle03Icon,
  audio: HeadphonesIcon,
  retail: ShoppingCart01Icon,
}

const CHANNEL_STYLE: Record<OverviewChannelKey, {
  text: string
  bar: string
  tile: string
}> = {
  search: {
    text: 'text-ch-search',
    bar: 'bg-ch-search',
    tile: 'bg-[image:var(--tile-search)]',
  },
  social: {
    text: 'text-ch-social',
    bar: 'bg-ch-social',
    tile: 'bg-[image:var(--tile-social)]',
  },
  tv: {
    text: 'text-ch-tv',
    bar: 'bg-ch-tv',
    tile: 'bg-[image:var(--tile-tv)]',
  },
  programmatic: {
    text: 'text-ch-programmatic',
    bar: 'bg-ch-programmatic',
    tile: 'bg-[image:var(--tile-programmatic)]',
  },
  audio: {
    text: 'text-ch-audio',
    bar: 'bg-ch-audio',
    tile: 'bg-[image:var(--tile-audio)]',
  },
  retail: {
    text: 'text-ch-retail',
    bar: 'bg-ch-retail',
    tile: 'bg-[image:var(--tile-retail)]',
  },
}

function fmtUsd(n: number): string {
  if (n === 0) return '$0'
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

export function OverviewV3Page({
  channels,
  initialIsExample = false,
}: {
  channels: OverviewChannel[]
  initialIsExample?: boolean
}) {
  const { isLoggedIn, isLoading: authLoading } = useCachedUser()
  const [channelFilter, setChannelFilter] = useState<ChannelFilterValue>('all')
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => ({
    preset: 'last_30',
    from: subDays(new Date(), 30),
    to: new Date(),
  }))
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [currentChannels, setCurrentChannels] = useState(channels)
  const [loading, setLoading] = useState(false)
  const [showingExample, setShowingExample] = useState(initialIsExample)
  const channelOptions = useMemo(
    () => currentChannels.map((channel) => ({
      value: channel.key as ChannelFilterValue,
      label: channel.name,
      active: channel.live > 0,
    })),
    [currentChannels],
  )
  const visibleChannels = useMemo(
    () => channelFilter === 'all'
      ? currentChannels
      : currentChannels.filter((channel) => channel.key === channelFilter),
    [channelFilter, currentChannels],
  )
  const filteredTotals = useMemo(() => {
    const spend = visibleChannels.reduce((sum, channel) => sum + channel.spend, 0)
    const revenue = visibleChannels.reduce((sum, channel) => sum + Math.round(channel.spend * channel.roas), 0)
    const roas = spend > 0 ? revenue / spend : 0
    const connectorsLive = visibleChannels.reduce((sum, channel) => sum + channel.live, 0)
    const connectorsTotal = visibleChannels.reduce((sum, channel) => sum + channel.total, 0)
    const channelsLive = visibleChannels.filter((channel) => channel.live > 0).length
    return { spend, revenue, roas, connectorsLive, connectorsTotal, channelsLive }
  }, [visibleChannels])

  useEffect(() => {
    if (authLoading) return

    if (!isLoggedIn) {
      setCurrentChannels(FALLBACK_OVERVIEW)
      setShowingExample(true)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const apiRange = dateRange.preset === 'last_7'
          ? '7d'
          : dateRange.preset === 'last_30'
            ? '30d'
            : '90d'
        const params = new URLSearchParams({ range: apiRange })
        if (channelFilter !== 'all') params.set('channel', channelFilter)
        // TODO: when /api/connectors/catalog/metrics supports from/to + channel filter, pass dateRange.from/to/channelFilter directly.
        const [catalogRes, metricsRes] = await Promise.all([
          fetch('/api/connectors/catalog'),
          fetch(`/api/connectors/catalog/metrics?${params.toString()}`, { cache: 'no-store' }),
        ])
        if (!catalogRes.ok || !metricsRes.ok) throw new Error('Failed to load overview')
        const [catalog, metrics] = await Promise.all([
          catalogRes.json() as Promise<OverviewCatalogResponse>,
          metricsRes.json() as Promise<ChannelMetricsResponse>,
        ])
        if (cancelled) return
        const nextChannels = adaptOverviewData(catalog, metrics)
        setCurrentChannels(nextChannels)
        setShowingExample(false)
      } catch {
        if (!cancelled) {
          setCurrentChannels([])
          setShowingExample(false)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [authLoading, channelFilter, dateRange, isLoggedIn])

  return (
    <div className="min-h-full bg-surface-0">
      <LobbyHeaderMeta>
        {filteredTotals.channelsLive} channel{filteredTotals.channelsLive === 1 ? '' : 's'} live ·{' '}
        {filteredTotals.connectorsLive} connectors
      </LobbyHeaderMeta>

      <div className="mx-auto max-w-[1040px] px-[26px] pb-[70px] pt-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-3.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 text-[28px] font-bold tracking-[-0.02em]">Overview</h1>
            {showingExample && <DemoDataBadge />}
          </div>
          <div className="flex flex-nowrap gap-2">
            <ChannelFilter value={channelFilter} onChange={setChannelFilter} options={channelOptions} />
            <DateRangeFilter value={dateRange} onChange={setDateRange} />
          </div>
        </div>

        {/* KPI band */}
        <div className={cn('mt-[18px] grid grid-cols-4 gap-3 transition-opacity max-[900px]:grid-cols-2', loading && 'opacity-60')}>
          {/* TODO: Real ▲ deltas need a second previous-window metrics query. */}
          <OvTile
            label="Spend"
            icon={<HugeiconsIcon icon={DollarCircleIcon} className="size-4" />}
            value={fmtUsd(filteredTotals.spend)}
            delta={showingExample ? '▲ 8.2% vs prev 30d' : '—'}
            deltaMuted={!showingExample}
          />
          <OvTile
            label="Blended ROAS"
            icon={<HugeiconsIcon icon={Target02Icon} className="size-4" />}
            value={filteredTotals.roas > 0 ? `${filteredTotals.roas.toFixed(2)}×` : '—'}
            delta={showingExample ? '▲ 0.18× vs prev 30d' : '—'}
            deltaMuted={!showingExample}
          />
          <OvTile
            label="Attributed revenue"
            icon={<HugeiconsIcon icon={ChartLineData01Icon} className="size-4" />}
            value={fmtUsd(filteredTotals.revenue)}
            delta={showingExample ? '▲ 11.4% vs prev 30d' : '—'}
            deltaMuted={!showingExample}
          />
          <OvTile
            label="Connectors live"
            icon={<HugeiconsIcon icon={PlugSocketIcon} className="size-4" />}
            value={`${filteredTotals.connectorsLive}/${filteredTotals.connectorsTotal}`}
            delta={`across ${filteredTotals.channelsLive} channel${filteredTotals.channelsLive === 1 ? '' : 's'}`}
            deltaMuted
          />
        </div>

        {/* By-channel rollup */}
        <div className={cn('mt-[30px] transition-opacity', loading && 'opacity-60')}>
          <SectionHeading label="By channel" title="Per-channel rollup" />
          {visibleChannels.map((c) => (
            <ChannelRollupCard
              key={c.key}
              channel={c}
              expanded={expandedKey === c.key}
              onToggle={() => setExpandedKey((current) => current === c.key ? null : c.key)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function OvTile({
  label,
  icon,
  value,
  delta,
  deltaMuted,
}: {
  label: string
  icon: React.ReactNode
  value: string
  delta: string
  deltaMuted?: boolean
}) {
  const isDown = delta.startsWith('▼')
  return (
    <div className="overflow-hidden rounded-card border-[0.5px] border-line bg-surface-1 pb-[15px]">
      <div className="flex items-center gap-1.5 px-4 pt-3.5 text-[13px] font-medium text-text">{icon}{label}</div>
      <div className="px-4 pt-2 font-mono text-[30px] font-extrabold leading-none tracking-[-0.04em] tabular-nums">{value}</div>
      <div
        className={cn(
          'px-4 pt-1.5 font-mono text-[11px]',
          deltaMuted ? 'text-text-muted' : isDown ? 'text-danger-ink' : 'text-success-fg',
        )}
      >
        {delta}
      </div>
    </div>
  )
}

function ChannelRollupCard({
  channel,
  expanded,
  onToggle,
}: {
  channel: OverviewChannel
  expanded: boolean
  onToggle: () => void
}) {
  const hasSubs = channel.live > 0 && channel.subs.length > 0
  const maxRoas = Math.max(1, ...channel.subs.map((s) => s.roas))
  const revenue = Math.round(channel.spend * channel.roas)
  const icon = CHANNEL_ICON[channel.key]
  const style = CHANNEL_STYLE[channel.key]

  return (
    <div className="mb-[18px] rounded-card border-[0.5px] border-line bg-surface-1 px-[18px] py-4">
      <div
        className={cn('flex flex-wrap items-center gap-[13px]', hasSubs && 'cursor-pointer')}
        onClick={hasSubs ? onToggle : undefined}
      >
        <div className={cn('flex size-[42px] shrink-0 items-center justify-center rounded-tile text-white', style.tile)}>
          <HugeiconsIcon icon={icon} className="size-5" />
        </div>
        <div>
          <div className={cn('text-base font-bold', !hasSubs && 'text-text-subtle')}>{channel.name}</div>
          <div className="mt-px font-mono text-[11px] text-text-muted">
            {channel.live > 0
              ? `${channel.live} active campaign${channel.live === 1 ? '' : 's'}`
              : 'No active campaigns'}
          </div>
        </div>
        {channel.live > 0 ? (
          <>
            <div className="ml-auto flex gap-7 text-right max-[640px]:hidden">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-text-muted">Spend</div>
                <div className="mt-[3px] font-mono text-xl font-extrabold tracking-[-0.03em] tabular-nums">{fmtUsd(channel.spend)}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-text-muted">Blended ROAS</div>
                <div className={cn('mt-[3px] font-mono text-xl font-extrabold tracking-[-0.03em] tabular-nums', style.text)}>
                  {channel.roas.toFixed(1)}×
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-text-muted">Revenue</div>
                <div className="mt-[3px] font-mono text-xl font-extrabold tracking-[-0.03em] tabular-nums">{fmtUsd(revenue)}</div>
              </div>
            </div>
            <div className="hidden max-[640px]:mt-3 max-[640px]:grid max-[640px]:basis-full max-[640px]:grid-cols-3 max-[640px]:gap-2 max-[640px]:text-right">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-text-muted">Spend</div>
                <div className="mt-[3px] font-mono text-sm font-extrabold tracking-[-0.03em] tabular-nums">{fmtUsd(channel.spend)}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-text-muted">ROAS</div>
                <div className={cn('mt-[3px] font-mono text-sm font-extrabold tracking-[-0.03em] tabular-nums', style.text)}>
                  {channel.roas.toFixed(1)}×
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-text-muted">Revenue</div>
                <div className="mt-[3px] font-mono text-sm font-extrabold tracking-[-0.03em] tabular-nums">{fmtUsd(revenue)}</div>
              </div>
            </div>
            <HugeiconsIcon
              icon={expanded ? ArrowUp01Icon : ArrowDown01Icon}
              className={cn('ml-[18px] shrink-0 text-lg text-meta-6 transition-transform duration-200', expanded && 'rotate-180')}
            />
          </>
        ) : (
          <Link
            href={`/marketplace?channel=${channel.key}`}
            className="ml-auto rounded-ctrl border border-brand bg-surface-1 px-[13px] py-[7px] text-xs font-bold text-brand no-underline"
          >
            Marketplace
          </Link>
        )}
      </div>
      {hasSubs && (
        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-200 ease-out',
            expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="overflow-hidden">
            <div className="mt-3.5 flex flex-col gap-2.5 border-t-[0.5px] border-line-subtle pt-[13px]">
              {channel.subs.map((sub) => {
                const width = Math.round((sub.roas / maxRoas) * 100)
                return (
                  <div key={sub.name} className="flex items-center gap-3">
                    <div className="w-40 truncate whitespace-nowrap text-[13px] font-semibold">{sub.name}</div>
                    <div
                      className="h-2 flex-1 overflow-hidden rounded-lg bg-surface-2"
                      aria-hidden="true"
                    >
                      <div className={cn('h-full', style.bar)} style={{ width: `${width}%` }} />
                    </div>
                    <div className={cn('w-[46px] text-right font-mono text-[13px] font-bold tabular-nums', style.text)}>
                      {sub.roas.toFixed(1)}×
                    </div>
                    <div className="w-[78px] text-right font-mono text-xs text-text-muted tabular-nums">{fmtUsd(sub.spend)}</div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
